/**
 * Tag hierarchy helpers — pure functions for building and resolving
 * hierarchical tag paths from flat tag arrays with parentId references.
 *
 * Extracted into a standalone module for testability. Used by useSyncNoteTags
 * and the server reconciliation flow.
 */

export interface HierarchicalTag {
  id: string
  name: string
  parentId?: string
}

/**
 * Build a full path for a tag by walking up the parentId chain.
 * e.g. tag {name:"project", parentId:"abc"} where "abc" is {name:"work"} → "work/project"
 */
export function tagFullPath<T extends HierarchicalTag>(
  tag: T,
  byId: Map<string, T>,
  seen?: Set<string>,
): string {
  if (!tag.parentId) return tag.name
  const parent = byId.get(tag.parentId)
  if (!parent) return tag.name
  const visited = seen ?? new Set<string>()
  if (visited.has(parent.id)) return tag.name
  visited.add(parent.id)
  return tagFullPath(parent, byId, visited) + '/' + tag.name
}

/**
 * Build a map of full-path (lowercased) → tag for all tags.
 * Used to match hashtag strings like "work/project" to existing tag records.
 */
export function buildFullPathMap<T extends HierarchicalTag>(
  tags: T[],
): Map<string, T> {
  const byId = new Map(tags.map((t) => [t.id, t]))
  const map = new Map<string, T>()
  for (const t of tags) {
    map.set(tagFullPath(t, byId).toLowerCase(), t)
  }
  return map
}
